import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"

export default class extends Controller {
  static targets = [
    "autoExitSettings",
    "visibilityInfo",
    "currentBeds",
    "capacityFeedback",
    "autoExitStatus",
    "autoExitPreview",
    "timeZone"
  ]

  static values = {
    networkId: String,
    organizationId: String,
    newRecord: Boolean
  }

  // Each state's majority zone, by the names Rails gives the US zones.
  static timeZoneByState = Object.fromEntries([
    ["Eastern Time (US & Canada)", "CT DC DE FL GA IN KY MA MD ME MI NC NH NJ NY OH PA RI SC VA VT WV"],
    ["Central Time (US & Canada)", "AL AR IA IL KS LA MN MO MS ND NE OK SD TN TX WI"],
    ["Mountain Time (US & Canada)", "CO ID MT NM UT WY"],
    ["Arizona", "AZ"],
    ["Pacific Time (US & Canada)", "CA NV OR WA"],
    ["Alaska", "AK"],
    ["Hawaii", "HI"]
  ].flatMap(([zone, states]) => states.split(" ").map((state) => [state, zone])))

  static defaultTimeZone = "Central Time (US & Canada)"

  // A new facility's zone follows its address's state until someone picks
  // one; the region input fires this whether typed or autocompleted.
  suggestTimeZone(event) {
    if (!this.newRecordValue || !this.hasTimeZoneTarget) return
    if (!event.target.name?.endsWith("[address_attributes][region]")) return
    const select = this.timeZoneTarget
    if (select.value !== this.constructor.defaultTimeZone) return

    const zone = this.constructor.timeZoneByState[event.target.value.trim().toUpperCase()]
    if (!zone || zone === select.value) return
    if (select.tomselect) select.tomselect.setValue(zone)
    else select.value = zone
  }

  toggleAutoExitSettings(event) {
    const facilityType = event.target.value;
    const autoExitSettings = this.autoExitSettingsTarget;

    if (facilityType === 'night_by_night') {
      autoExitSettings.classList.remove('hidden');
    } else {
      autoExitSettings.classList.add('hidden');
    }
  }

  updateVisibilityInfo(event) {
    const visibilityType = event.target.value;
    const visibilityInfo = this.visibilityInfoTarget;
    const hiddenField = this.element.querySelector('input[name="shelter_facility[visible_to_id]"]');

    if (visibilityType === 'Network') {
      // Update hidden field for network visibility
      hiddenField.value = this.networkIdValue;
      // Update info text
      visibilityInfo.innerHTML = `
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <path d="m12 16 4-4-4-4"/>
          <path d="m8 12 4 4"/>
        </svg>
        <div>
          <h4 class="font-medium">${i18n.t('facility_form.visibility_impact')}</h4>
          <div class="text-sm mt-1">
            <p>${i18n.t('facility_form.network_visibility')}</p>
            <ul class="list-disc list-inside mt-1 ml-2">
              <li>${i18n.t('facility_form.network_bed_availability')}</li>
              <li>${i18n.t('facility_form.network_utilization_reports')}</li>
              <li>${i18n.t('facility_form.network_coordinated_entry')}</li>
            </ul>
          </div>
        </div>
      `;
    } else {
      // Update hidden field for organization visibility
      hiddenField.value = this.organizationIdValue;
      // Update info text
      visibilityInfo.innerHTML = `
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <path d="m12 16 4-4-4-4"/>
          <path d="m8 12 4 4"/>
        </svg>
        <div>
          <h4 class="font-medium">${i18n.t('facility_form.visibility_impact')}</h4>
          <div class="text-sm mt-1">
            <p>${i18n.t('facility_form.org_visibility')}</p>
            <ul class="list-disc list-inside mt-1 ml-2">
              <li>${i18n.t('facility_form.org_bed_management')}</li>
              <li>${i18n.t('facility_form.org_reporting')}</li>
              <li>${i18n.t('facility_form.org_private_operations')}</li>
            </ul>
          </div>
        </div>
      `;
    }
  }

  validateCapacity(event) {
    const capacity = parseInt(event.target.value);
    const currentBeds = parseInt(this.currentBedsTarget?.textContent || 0);
    const feedback = this.capacityFeedbackTarget;

    if (capacity && currentBeds && capacity < currentBeds) {
      feedback.textContent = `• ${i18n.t('facility_form.capacity_minimum', { count: currentBeds })}`;
      feedback.className = 'text-red-600';
    } else {
      feedback.textContent = '';
      feedback.className = 'text-green-600';
    }
  }

  setAutoExitDays(event) {
    const days = event.target.dataset.days;
    const input = this.element.querySelector('input[name="shelter_facility[auto_exit_days]"]');
    input.value = days;

    // Trigger the input event to update status
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  updateAutoExitInfo(event) {
    const days = event.target.value;
    const status = this.autoExitStatusTarget;

    if (days) {
      status.innerHTML = `
        <div class="flex items-center">
          <span class="badge badge-success badge-sm">
            <svg class="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
            ${i18n.t('facility_form.enabled')}
          </span>
          <span class="ml-2 text-sm text-gray-600">${i18n.t('facility_form.days', { count: days })}</span>
        </div>
      `;
    } else {
      status.innerHTML = `
        <span class="badge badge-ghost badge-sm">
          <svg class="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
          ${i18n.t('facility_form.disabled')}
        </span>
      `;
    }
  }
}